import { useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { css } from "../../../styled-system/css";
import { HStack, VStack } from "../../../styled-system/jsx";
import { Modal } from "../../components/core/basic/Modal";
import { Select, SelectItem } from "../../components/fields/base/Select";
import FileDropZone from "../../components/core/blocks/FileDropZone";
import { apiClient } from "../../zodios/client";
import { useAppSelector } from "../../redux/hooks";
import { useOrgMarketplaceKeys } from "../admin/api/useOrgMarketplaces";
import { useMarketplaceOptions } from "../../utils/marketplaceConstants";
import { uploadToS3 } from "../uploads/uploadToS3";
import { useUpload } from "../uploads/api/useUpload";
import { isTerminalStatus } from "../uploads/constants";
import { StatusBadge } from "../uploads/StatusBadge";
import { UploadStatusView } from "../uploads/UploadStatusView";
import { errorToast, successToast } from "../../utils/toasts";
import campaignBulksheetTemplateUrl from "/campaign_bulksheet_template.csv?url";
import campaignBulksheetTemplateWithExamplesUrl from "/campaign_bulksheet_template_with_examples.csv?url";

const MAX_ROWS = 50;
const FILE_EXCEEDS_MAX_ROWS_MARKER = "FILE_EXCEEDS_MAX_ROWS";

function FileUploadForm({ onUploadCreated }: { onUploadCreated: (id: number) => void }) {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["advertising"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { data: availableMarketplaces } = useOrgMarketplaceKeys();
  const marketplaceOptions = useMarketplaceOptions();
  const filteredOptions =
    availableMarketplaces ?
      marketplaceOptions.filter((opt) => availableMarketplaces.includes(opt.value))
    : marketplaceOptions;

  const [selectedMarketplace, setSelectedMarketplace] = useState(marketPlace);

  const uploadMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      // Strip comment lines (# prefix) and empty lines
      const rawText = await file.text();
      const sanitizedFileText = rawText
        .split(/\r?\n/)
        .filter((line) => {
          const trimmed = line.trim();
          if (!trimmed) return false;
          // Strip leading quote — CSV exporters wrap comment lines containing quotes
          const unquoted = trimmed.startsWith('"') ? trimmed.slice(1) : trimmed;
          return !unquoted.startsWith("#");
        })
        .join("\n");

      // Count data rows (excluding header)
      const lines = sanitizedFileText.split(/\r?\n/).filter((line) => line.trim());
      const rowCount = lines.length - 1; // Exclude header

      if (rowCount > MAX_ROWS) {
        throw new Error(`${FILE_EXCEEDS_MAX_ROWS_MARKER}:${rowCount}`);
      }

      const cleanedFile = new File([sanitizedFileText], file.name, { type: file.type });
      const id = await uploadToS3({ file: cleanedFile, uploadType: "bulk_upload" });

      await apiClient.processUpload(
        {
          marketplace: selectedMarketplace,
          async_run: true,
          ignoreWarnings: false,
          autoProcess: false,
        },
        { params: { id } },
      );

      // Invalidate so UploadsPage picks up the new active upload and starts polling
      void queryClient.invalidateQueries({ queryKey: [{ entity: "uploads" }] });

      return id;
    },
    onSuccess: (id) => {
      onUploadCreated(id);
    },
    onError: (error) => {
      const message =
        error.message.startsWith(FILE_EXCEEDS_MAX_ROWS_MARKER) ?
          t(($) => $.advertising["File exceeds maximum of {max} rows (found {count})."], {
            max: MAX_ROWS,
            count: Number(error.message.split(":")[1]),
          })
        : t(($) => $.advertising["An unexpected error occurred. Please try again."]);
      errorToast({
        title: t(($) => $.advertising["Upload failed"]),
        message,
        error,
      });
    },
  });

  const handleFileSelection = (file: File) => {
    uploadMutation.mutate({ file });
  };

  return (
    <>
      <Select
        label={t(($) => $.advertising["Marketplace"])}
        selectedKey={selectedMarketplace}
        onSelectionChange={(key) => setSelectedMarketplace(key as string)}
        items={filteredOptions}
        isDisabled={uploadMutation.isPending}
      >
        {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
      </Select>

      <HStack gap="small">
        <a href={campaignBulksheetTemplateUrl} download>
          {t(($) => $.advertising["Clean Template"])}
        </a>
        <span>•</span>
        <a href={campaignBulksheetTemplateWithExamplesUrl} download>
          {t(($) => $.advertising["With Examples"])}
        </a>
      </HStack>

      <p className={css({ color: "gray.600", fontSize: "small" })}>
        {t(
          ($) =>
            $.advertising[
              "Upload a CSV file with up to {max} rows. The file will be validated and processed for the selected marketplace."
            ],
          { max: MAX_ROWS },
        )}
      </p>

      <FileDropZone
        onFileSelect={handleFileSelection}
        disabled={uploadMutation.isPending}
        label={t(($) => $.advertising["Drop your CSV file here or click the button below"])}
        isPending={uploadMutation.isPending}
        acceptedFileTypes={[".csv"]}
      />
    </>
  );
}

export const CampaignBulksheetUploadModal = NiceModal.create(({ uploadId }: { uploadId?: number }) => {
  const modal = useModal();
  const { t } = useTranslation(["advertising"]);
  const [activeUploadId, setActiveUploadId] = useState(uploadId);
  const { data: upload } = useUpload(activeUploadId);

  const isProcessing = !!activeUploadId && !!upload && !isTerminalStatus(upload.status);

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          if (isProcessing) {
            successToast({
              title: t(($) => $.advertising["Upload continues in the background"]),
              message: t(($) => $.advertising["You can track progress on the Uploads page."]),
            });
          }
          modal.remove();
        }
      }}
      title={
        <HStack gap="medium" alignItems="center">
          <span>{t(($) => $.advertising["Campaign Bulksheet Upload"])}</span>
          {upload && <StatusBadge status={upload.status} />}
        </HStack>
      }
      css={{ minWidth: "800px" }}
    >
      <VStack
        gap="large"
        alignItems="stretch"
        paddingY="xlarge"
        paddingX="xlarge"
        overflow="auto"
        minHeight="0"
        flex="1"
      >
        {activeUploadId ?
          <UploadStatusView
            uploadId={activeUploadId}
            onClose={() => modal.remove()}
            successMessage={t(
              ($) => $.advertising["Your campaign bulksheet has been successfully uploaded and processed."],
            )}
            confirmationPhrase="upload bulksheet"
          />
        : <FileUploadForm onUploadCreated={setActiveUploadId} />}
      </VStack>
    </Modal>
  );
});
